import type { InputHTMLAttributes, ReactElement, SelectHTMLAttributes } from 'react';

/**
 * One shape for every control, so a form is a column of identical objects.
 *
 * The height is 36px — the same as the default `Button`, which is what lets a
 * field and the button beside it sit on one line without either looking like a
 * mistake. `aria-[invalid=true]` rather than a `hasError` prop: `Field` already
 * sets that attribute for the assistive-technology half of the problem, and driving the visual
 * from the same attribute means the two cannot disagree about which control is
 * in error.
 *
 * The transition names its properties: `transition-all` on an input animates
 * width, and a field that slides when the layout reflows reads as broken.
 */
const BASE =
  'w-full rounded-[var(--radius-md)] border border-line bg-surface px-3 py-[6px] text-[length:var(--text-sm)] text-body transition-[border-color,box-shadow] duration-[var(--motion-fast)] ease-[var(--ease-out)] placeholder:text-muted hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-danger';

export function Input(props: InputHTMLAttributes<HTMLInputElement>): ReactElement {
  const { className = '', ...rest } = props;
  return <input className={`${BASE} h-9 ${className}`} {...rest} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>): ReactElement {
  const { className = '', children, ...rest } = props;
  // Deliberately still a native `<select>`: an `<option>` cannot hold a
  // graphic, and rewriting it into a listbox to gain one would be a behaviour
  // change — keyboard type-ahead, the mobile picker, the platform's own
  // rendering — dressed up as a visual one.
  return (
    <select className={`${BASE} h-9 ${className}`} {...rest}>
      {children}
    </select>
  );
}

export function Checkbox(props: InputHTMLAttributes<HTMLInputElement>): ReactElement {
  const { className = '', ...rest } = props;
  return (
    <input
      type="checkbox"
      // `border-line-strong`: an unchecked box outlined in the interior-divider
      // hairline is invisible at 16px, and an invisible checkbox in a table of
      // a thousand SIMs is a selection an operator cannot verify.
      className={`size-4 shrink-0 rounded-[var(--radius-sm)] border-line-strong text-primary accent-primary ${className}`}
      {...rest}
    />
  );
}
